/**
 * Shared answer-grading utilities.
 *
 * Used by both the client (Learning.tsx) and the server (routes.ts) so the
 * "Correct!" / "Not quite right" feedback the student sees always matches
 * the `isCorrect` value recorded in the database.
 *
 * Historical notes:
 * - `correct_answer` on the questions table is a `text` column. For compound
 *   types it has been stored in several shapes over time:
 *     multi-select : "a,c,d"           OR  '["a","c","d"]'
 *     matching     : '{"p1":"a2",...}'
 *     order-match  : "a,b,c,d"          OR  the source-of-truth is the option order itself
 *     fill-blank   : "Volcano"          OR  "Volcano,Active volcano"  OR '["Volcano","Active volcano"]'
 *
 * - User-submitted `selectedAnswer` arrives in matching shapes (see handleSubmitAnswer).
 *
 * - For per-question alternate-spelling support on fill-blank, callers can
 *   put a JSON array in `correctAnswer` (e.g. '["glass","Glass","sheet of glass"]')
 *   instead of relying on comma-splitting (which breaks for answers like "7,923").
 */

export type GradableQuestion = {
  questionType: string;
  correctAnswer: string;
  options?: any;
  /** Optional strict flag — when true, fill-blank comparisons are case-sensitive. */
  strict?: boolean;
};

/** Try to parse a string as JSON, returning undefined on failure. */
function tryParseJson(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return undefined;
  }
}

/** Normalise a free-text answer: trim, lowercase, collapse internal whitespace. */
function normaliseText(s: string, caseSensitive = false): string {
  const trimmed = String(s ?? "").trim().replace(/\s+/g, " ");
  return caseSensitive ? trimmed : trimmed.toLowerCase();
}

/**
 * Parse the stored correct_answer for a multi-select question into a sorted
 * unique array of option-id strings.
 *
 * Supports:
 *   - JSON array: '["a","c","d"]'
 *   - Comma-separated: "a,c,d"
 *   - Single value: "a"
 */
export function parseMultiSelectKey(correctAnswer: string): string[] {
  const parsed = tryParseJson(correctAnswer);
  let list: any[];
  if (Array.isArray(parsed)) {
    list = parsed;
  } else if (parsed !== undefined && parsed !== null && typeof parsed !== "object") {
    list = [parsed];
  } else if (typeof correctAnswer === "string" && correctAnswer.includes(",")) {
    list = correctAnswer.split(",");
  } else {
    list = [correctAnswer];
  }
  return Array.from(new Set(list.map((x) => String(x).trim()))).sort();
}

/** Parse the user's multi-select submission (JSON array, comma-string, or array). */
export function parseMultiSelectSubmission(userAnswer: unknown): string[] {
  let list: any[];
  if (Array.isArray(userAnswer)) {
    list = userAnswer;
  } else if (typeof userAnswer === "string") {
    const parsed = tryParseJson(userAnswer);
    if (Array.isArray(parsed)) {
      list = parsed;
    } else if (userAnswer.includes(",")) {
      list = userAnswer.split(",");
    } else {
      list = [userAnswer];
    }
  } else {
    list = [];
  }
  return Array.from(
    new Set(list.map((x) => (x == null ? "" : String(x).trim())).filter((x) => x.length > 0))
  ).sort();
}

/**
 * Parse a matching answer (user or key) into a canonical sorted-by-key pair list.
 * Accepts either an object `{"p1":"a2",...}` or already-parsed object.
 */
export function parseMatchingAnswer(value: unknown): Array<[string, string]> {
  let obj: Record<string, any> | undefined;
  if (typeof value === "string") {
    const parsed = tryParseJson(value);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      obj = parsed;
    }
  } else if (value && typeof value === "object" && !Array.isArray(value)) {
    obj = value as Record<string, any>;
  }
  if (!obj) return [];
  return Object.keys(obj)
    .sort()
    .map((k) => [k, String(obj![k]).trim()] as [string, string]);
}

/** Determine the canonical order key for an order-match question from its options. */
function getOrderMatchKey(question: GradableQuestion): string {
  // Prefer correctAnswer if it looks like a serialized order; fall back to options order.
  if (question.correctAnswer && typeof question.correctAnswer === "string") {
    // If options exist and correctAnswer is just the options' IDs in order, both work.
    return question.correctAnswer.trim();
  }
  const opts = Array.isArray(question.options) ? question.options : [];
  return opts
    .map((opt: any, idx: number) => (typeof opt === "object" && opt?.id ? opt.id : `order_${idx}`))
    .join(",");
}

/**
 * Build the list of accepted answers for a fill-blank question.
 *
 * Order of preference:
 *   1. correctAnswer is a JSON array  -> use directly  (recommended for new questions)
 *   2. correctAnswer looks like a number with commas (e.g. "7,923") -> treat as single answer
 *      and ALSO accept the unformatted version "7923".
 *   3. correctAnswer contains a comma  -> split on comma (legacy alternate-spelling format)
 *   4. otherwise -> single accepted answer
 */
export function parseFillBlankKey(correctAnswer: string): string[] {
  const raw = String(correctAnswer ?? "");
  const parsed = tryParseJson(raw);
  if (Array.isArray(parsed)) {
    return parsed.map((x) => String(x));
  }

  // Looks like a comma-grouped number? Treat as one answer, with a digit-only fallback.
  // Matches things like "7,923" or "1,234,567" but not "apple,orange".
  if (/^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(raw.trim())) {
    const stripped = raw.trim().replace(/,/g, "");
    return [raw.trim(), stripped];
  }

  if (raw.includes(",")) {
    return raw.split(",").map((s) => s.trim()).filter((s) => s.length > 0);
  }
  return [raw];
}

/**
 * The single source of truth for "did this user answer this question correctly?"
 *
 * @param question  The question (questionType + correctAnswer + options + optional strict flag).
 * @param userAnswer  The user's submission. Type depends on questionType — see
 *                    parseMultiSelectSubmission / parseMatchingAnswer / etc.
 */
export function gradeAnswer(question: GradableQuestion, userAnswer: unknown): boolean {
  if (question == null || question.correctAnswer == null) return false;

  switch (question.questionType) {
    case "multi-select":
    case "multiple-select": {
      const submitted = parseMultiSelectSubmission(userAnswer);
      const key = parseMultiSelectKey(question.correctAnswer);
      if (submitted.length === 0 || submitted.length !== key.length) return false;
      return submitted.every((v, i) => v === key[i]);
    }

    case "matching": {
      const submitted = parseMatchingAnswer(userAnswer);
      const key = parseMatchingAnswer(question.correctAnswer);
      if (submitted.length === 0 || submitted.length !== key.length) return false;
      return submitted.every(([sk, sv], i) => sk === key[i][0] && sv === key[i][1]);
    }

    case "order-match":
    case "ordering": {
      // user-side may pass an array of IDs or a comma-joined string
      const userStr =
        Array.isArray(userAnswer) ? userAnswer.join(",") : String(userAnswer ?? "").trim();
      const keyStr = getOrderMatchKey(question);
      if (!userStr || !keyStr) return false;
      // Element-by-element compare to be tolerant of stray spaces around commas.
      const u = userStr.split(",").map((s) => s.trim());
      const k = keyStr.split(",").map((s) => s.trim());
      if (u.length !== k.length) return false;
      return u.every((v, i) => v === k[i]);
    }

    case "table-grid": {
      const userCells =
        Array.isArray(userAnswer)
          ? userAnswer.map(String)
          : String(userAnswer ?? "")
              .split(",")
              .filter(Boolean);
      const correctCells = String(question.correctAnswer)
        .split(",")
        .filter(Boolean);
      const u = userCells.map((x) => Number(x)).sort((a, b) => a - b);
      const k = correctCells.map((x) => Number(x)).sort((a, b) => a - b);
      if (u.length === 0 || u.length !== k.length) return false;
      return u.every((v, i) => v === k[i]);
    }

    case "fill-blank":
    case "short-answer": {
      const submittedRaw = Array.isArray(userAnswer)
        ? String(userAnswer[0] ?? "")
        : String(userAnswer ?? "");
      const caseSensitive = question.strict === true;
      const submitted = normaliseText(submittedRaw, caseSensitive);
      if (submitted.length === 0) return false;
      const accepted = parseFillBlankKey(question.correctAnswer).map((a) =>
        normaliseText(a, caseSensitive)
      );
      // Also try a digit-only variant of the submission to match comma-grouped numeric keys
      // when the student types the unformatted number ("7923" vs key "7,923").
      const submittedDigits = submittedRaw.replace(/[\s,]/g, "");
      const matchDirect = accepted.includes(submitted);
      const matchDigits =
        /^-?\d+(\.\d+)?$/.test(submittedDigits) &&
        accepted.some((a) => a.replace(/[\s,]/g, "") === submittedDigits);
      return matchDirect || matchDigits;
    }

    case "multiple-choice":
    case "true-false":
    default: {
      const u = typeof userAnswer === "string" ? userAnswer.trim() : String(userAnswer ?? "").trim();
      const k = String(question.correctAnswer ?? "").trim();
      return u === k;
    }
  }
}
